import './Stripe.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {loadStripe} from '@stripe/stripe-js';
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

import { ButtonLabelVerify, ButtonLabelVerifying, ButtonLabelVerified } from './ButtonLabels';
import { track } from './../analytics.js';
import { PayOnPhone } from './PhoneHandoff.js';

const VALID_SIZES = ['4x6', '6x9', '6x11'];
const sizeFromUrl = () => {
  const s = new URLSearchParams(window.location.search).get('size');
  return VALID_SIZES.includes(s) ? s : '4x6';
};

// Bare hostname of the artist site, for the newsletter checkbox label and the
// post-purchase return band.
const artistDomainFromUrl = () => {
  const artistUrl = new URLSearchParams(window.location.search).get('artistUrl');
  if (!artistUrl) return null;
  try { return new URL(artistUrl).hostname.replace(/^www\./, ''); }
  catch (e) { return artistUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, ''); }
};

// Order payload stashed in sessionStorage across a redirect-based payment
// (e.g. crypto/USDC bounces through an external page). Written just before
// stripe.confirmPayment; consumed by RedirectReturn on the way back; cleared
// on every in-page terminal outcome so it can't go stale.
const PENDING_ORDER_KEY = 'ollie_pending_order';
const PENDING_ORDER_TTL_MS = 60 * 60 * 1000;

// Discount a base cost by an applied promo (shape from /promo's response).
const applyPromoToCost = (base, promo) => {
  if (!promo || !promo.coupon) return base;
  let c = base;
  if (promo.coupon.percent_off != null) c = base - (base * (promo.coupon.percent_off / 100));
  else if (promo.coupon.amount_off != null) c = base - promo.coupon.amount_off;
  return Math.max(0, c);
};

// POST the finished order to /lob. Shared by the in-page flow (cards, wallets)
// and RedirectReturn (redirect-based methods).
async function postLob(body) {
  const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/lob', {
    method: "POST",
    body: JSON.stringify(body),
    mode: 'cors',
    credentials: 'same-origin',
    cache: 'no-cache',
    redirect: 'follow',
    headers: {
      'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN,
      "Content-Type": "application/json"
    }
  });
  return res.json();
}

// Route the Preview link through /?pdf=<postcardId> instead of pointing at Lob
// directly. Lob renders the PDF asynchronously after /postcards creation, so
// the raw url 404s for users who click within the first minute. The /?pdf=
// page polls and redirects once ready.
const receiptMessage = (email, decoded) => {
  const previewUrl = decoded.id
    ? `${window.location.origin}/?pdf=${encodeURIComponent(decoded.id)}`
    : decoded.url;
  return `Payment successful! You'll receive an email receipt shortly at ${email}. <a href="${previewUrl}" target="_blank" rel="noopener noreferrer">Preview your postcard here</a>. Please allow 10-14 business days for delivery.`;
};

// "Return to artist.com →" band shown once an order is fully placed.
const ReturnBand = () => {
  const artistUrl = new URLSearchParams(window.location.search).get('artistUrl');
  const domain = artistDomainFromUrl();
  if (!artistUrl || !domain) return null;
  return <a className="return-band" href={artistUrl}>Return to {domain} &rarr;</a>;
};

const CheckoutForm = (props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [ isLoading, setIsLoading ] = useState(false);
  const [ message, setMessage ] = useState(null);
  const [ messageType, setMessageType ] = useState(''); // 'error', 'success', or ''
  const [ paymentSuccessful, setPaymentSuccessful ] = useState(false);
  // True only once the postcard is actually submitted (the final receipt
  // message with the preview link is shown). The return-to-site band is tied
  // to this so it appears together with that message, not earlier at payment
  // confirmation.
  const [ orderComplete, setOrderComplete ] = useState(false);
  const [ promoCode, setPromoCode] = useState({});
  const [ promoInput, setPromoInput ] = useState(''); // New state for input value
  const [ promoValidating, setPromoValidating ] = useState(false); // New state for validation loading
  const [ cost, setCost ] = useState(0); // Start with 0, will be set from backend
  const [ defaultCost, setDefaultCost ] = useState(0); // Domain-specific default cost
  // Consent to share the buyer's email with the artist for their newsletter.
  // Must default to unchecked — this is explicit opt-in, not opt-out.
  const [ newsletterOptIn, setNewsletterOptIn ] = useState(false);
  // Whether this artist's domain offers the newsletter checkbox at all
  // (per-domain switch in the admin editor, reported by /cost; default off).
  const [ newsletterEnabled, setNewsletterEnabled ] = useState(false);

  // Destructure all props at component level to avoid linting errors
  const { billingDetails, setPaymentIntent, email, setEmail, payerName, setPayerName, paymentIntent } = props;
  const { onBusyChange, onOrderComplete } = props;

  // Tell the page when this device is mid-order or done. The "finish on your
  // phone" panel polls for this same order shipping, and must not mistake our
  // own purchase for one made on a phone; kiosk mode also times its return to
  // the artist page off these.
  useEffect(() => {
    if (onBusyChange) onBusyChange(isLoading || paymentSuccessful || orderComplete);
  }, [onBusyChange, isLoading, paymentSuccessful, orderComplete]);
  useEffect(() => {
    if (onOrderComplete) onOrderComplete(orderComplete);
  }, [onOrderComplete, orderComplete]);

  // Get domain-specific cost on component mount
  useEffect(() => {
    const fetchDomainCost = async () => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const artistUrl = urlSearchParams.get('artistUrl');
      const size = sizeFromUrl();

      try {
        const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/cost', {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN
          },
          body: JSON.stringify({
            artistUrl: artistUrl,
            nonce: document.querySelector('input[name="nonce"]').value,
            country: billingDetails.country || 'US',
            size: size,
          }),
          mode: 'cors',
          credentials: 'same-origin',
          cache: 'no-cache',
          redirect: 'follow'
        });

        const data = await response.json();
        setNewsletterEnabled(!!data.newsletter_optin);
        const base = data.cost || 80; // fallback to 80 cents if none returned
        setDefaultCost(base);
        // Country changes reprice the base cost; an applied promo must keep
        // discounting the NEW base or the displayed total silently reverts to
        // full price while the promo chip still shows as applied.
        setCost(applyPromoToCost(base, promoCode));
      } catch (error) {
        console.error('Error fetching domain cost:', error);
        // Fallback to 80 cents if error
        setDefaultCost(80);
        setCost(applyPromoToCost(80, promoCode));
      }
    };

    fetchDomainCost();
  }, [billingDetails.country, promoCode]); // Re-fetch when country changes

  // Sync the PaymentIntent's amount + promo metadata whenever the price
  // changes (country selection, promo applied/removed). The SAME PI is
  // updated for the life of the checkout — the Payment Element is bound to
  // its client_secret at mount, so recreating would need a remount that wipes
  // form state. elements.fetchUpdates() then refreshes the element (wallet
  // sheets show the PI's amount, and a stale one would display the wrong
  // price). Skipped under $0.50: Stripe rejects such amounts, and /lob ships
  // those orders off the /promo-written metadata instead.
  useEffect(() => {
    const syncPaymentIntent = async () => {
      if (!billingDetails.country || cost < 50 || !paymentIntent.id) {
        console.log('Skipping payment intent sync:', { country: billingDetails.country, cost });
        return;
      }

      console.log('Syncing payment intent with:', { country: billingDetails.country, cost });

      const urlSearchParams = new URLSearchParams(window.location.search);
      const artistUrl = urlSearchParams.get('artistUrl');
      const size = sizeFromUrl();

      try {
        const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/stripe', {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN
          },
          body: JSON.stringify({
            items: [{ id: "postcard-" + size }],
            nonce: document.querySelector('input[name="nonce"]').value,
            artistUrl: artistUrl,
            country: billingDetails.country,
            size: size,
            promoCodeId: promoCode.id || null,
            paymentIntentId: paymentIntent.id,
          }),
          mode: 'cors',
          credentials: 'same-origin',
          cache: 'no-cache',
          redirect: 'follow'
        });

        const data = await response.json();
        console.log('Payment intent sync response:', data);
        if (data.client_secret) {
          setPaymentIntent(data);
          if (elements) {
            await elements.fetchUpdates();
          }
        } else {
          console.error('No client_secret in payment intent response:', data);
        }
      } catch (error) {
        console.error('Error updating payment intent:', error);
      }
    };

    syncPaymentIntent();
  }, [cost, billingDetails.country, promoCode.id, paymentIntent.id, setPaymentIntent, elements]);

  const setMessageWithType = (msg, type = '') => {
    setMessage(msg);
    setMessageType(type);
  };

  // The /lob payload minus paymentIntent + nonce (both attached at send time).
  // Built once here so the in-page path and the pre-redirect stash agree.
  const buildLobBody = useCallback(() => {
    const urlSearchParams = new URLSearchParams(document.location.search);
    const size = sizeFromUrl();

    let body = {
      to: { ...billingDetails, email: email },
      email: email, // Also include at top level for backend compatibility
      promo : Object.keys(promoCode).length > 0 ? promoCode : null,
      merge_variables : {},
      country: billingDetails.country || 'US', // Pass country to backend
      size: size,
      newsletter_opt_in: newsletterOptIn,
      cost: cost,
    };

    if( urlSearchParams.get('optionalParams') && urlSearchParams.get('optionalParams').length > 0 ){
      try {
        // Simple URL decoding - no base64 involved
        const optionalParamsEncoded = urlSearchParams.get('optionalParams');
        const decodedOptionalParams = JSON.parse(decodeURIComponent(optionalParamsEncoded));
        body.merge_variables = decodedOptionalParams;
      } catch (error) {
        console.error('Error decoding optionalParams:', error);
        // Fall back to empty merge_variables if decoding fails
        body.merge_variables = {};
      }
    }

    if( urlSearchParams.get('imgUrl') && urlSearchParams.get('imgUrl').length > 0 ){
      body.merge_variables.artworkImageURL = urlSearchParams.get('imgUrl');
    }

    if( urlSearchParams.get('artistUrl') && urlSearchParams.get('artistUrl').length > 0 ){
      body.artistUrl = urlSearchParams.get('artistUrl');
    }

    return body;
  }, [billingDetails, email, promoCode, cost, newsletterOptIn]);

  // Same payload the in-page path sends to /lob, saved server-side for a
  // phone that scans the pay-step QR.
  const handoffOrder = useMemo(() => buildLobBody(), [buildLobBody]);

  const sendPostcard = useCallback(async (paymentIntentArg) => {
    const body = buildLobBody();
    body.paymentIntent = paymentIntentArg === null ? paymentIntent : paymentIntentArg;
    body.nonce = document.querySelector('input[name="nonce"]').value;

    console.log('Sending to backend:', JSON.stringify(body, null, 2));

    try {
      const lobResponseDecoded = await postLob(body);

      console.log('Backend response:', lobResponseDecoded);

      if( lobResponseDecoded.url ){
        setMessageWithType(receiptMessage(email, lobResponseDecoded), 'success');
        setOrderComplete(true);
        track('purchase_completed', {
          cost: lobResponseDecoded.cost ?? cost,
          original_cost: lobResponseDecoded.original_cost,
          country: billingDetails.country || 'US',
          promo_used: !!(promoCode && promoCode.code),
          promo_code: (promoCode && promoCode.code) || null,
          postcard_id: lobResponseDecoded.id,
        });
      }
      else {
        setMessageWithType('Your payment went through but we hit a problem submitting your postcard. Please contact support@olliemail.net and we\'ll sort it out.', 'error');
      }

    } catch (error) {
      console.error('An error occurred:', error);
      setMessageWithType('An error occurred while processing your order. Please try again.', 'error');
    }
  }, [buildLobBody, paymentIntent, email, cost, billingDetails, promoCode]);

  const validatePromoCode = async () => {
    if (promoInput.trim().length === 0) {
      setMessageWithType('Please enter a promo code', 'error');
      return;
    }

    setPromoValidating(true);
    setMessageWithType('');

    const urlSearchParams = new URLSearchParams(window.location.search);
    const artistUrl = urlSearchParams.get('artistUrl');
    const size = sizeFromUrl();

    const promoBody = {
      promo: promoInput.trim(),
      nonce: document.querySelector('input[name="nonce"]').value,
      paymentIntent: paymentIntent,
      artistUrl: artistUrl, // Include artistUrl for domain-specific calculations
      country: billingDetails.country || 'US', // Include country for pricing
      size: size, // Include size so promo discount applies to the right base price
    };

    try {
      const promoResponse = await fetch(process.env.REACT_APP_BACKEND_URL + '/promo', {
        method: "POST",
        body: JSON.stringify(promoBody),
        mode: 'cors',
        credentials: 'same-origin',
        cache: 'no-cache',
        redirect: 'follow',
        headers: {
          'Access-Control-Allow-Origin': process.env.REACT_APP_FRONTEND_ORIGIN,
          "Content-Type": "application/json"
        }
      });

      const promoResponseDecoded = await promoResponse.json();

      if (promoResponseDecoded.result === 'error') {
        setMessageWithType(promoResponseDecoded.message, 'error');
        setCost(defaultCost);
        setPromoCode({});
      } else if (promoResponseDecoded.active) {
        setPromoCode(promoResponseDecoded);

        // Calculate new cost using domain-specific default
        let newCost = defaultCost;
        if (promoResponseDecoded.coupon.percent_off !== null) {
          newCost = defaultCost - (defaultCost * (promoResponseDecoded.coupon.percent_off / 100));
        } else if (promoResponseDecoded.coupon.amount_off !== null) {
          newCost = defaultCost - promoResponseDecoded.coupon.amount_off;
        }

        newCost = Math.max(0, newCost); // Ensure cost doesn't go below 0

        setCost(newCost);

        track('promo_applied', {
          promo_code: promoResponseDecoded.code,
          original_cost: defaultCost,
          discounted_cost: newCost,
          discount: defaultCost - newCost,
        });
        // The cost change above triggers the sync effect, which re-updates the
        // same PI (amount + promo metadata) and refreshes the Payment Element
        // via fetchUpdates. Sub-$0.50 discounts skip the sync and /lob reads
        // the promo from the metadata /promo just wrote.
      }
    } catch (error) {
      console.log(error);
      setMessageWithType('Error validating promo code. Please try again.', 'error');
      setCost(defaultCost);
      setPromoCode({});
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromoCode = () => {
    setPromoInput('');
    setPromoCode({});
    setCost(defaultCost);
    // The cost/promo change retriggers the sync effect, which restores the
    // PI's full amount and clears its promo metadata (so /lob can't ship it
    // as a discounted order).
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate payer name
    if( payerName.length === 0 ){
      setMessageWithType('Please enter your name for the payment.', 'error');
      return;
    }

    // Validate email before processing payment
    if( email.length === 0 ){
      setMessageWithType('Please enter your email address to receive the receipt.', 'error');
      return;
    }
    else if( email.match(
      // eslint-disable-next-line no-useless-escape
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    ) === null ){
      setMessageWithType('Please enter a valid email address.', 'error');
      return;
    }

    if (!stripe) {
      return;
    }

    setIsLoading(true);
    setMessageWithType(''); // Clear any existing messages

    track('payment_attempted', {
      cost: cost,
      country: billingDetails.country || 'US',
      promo_used: !!(promoCode && promoCode.code),
    });

    // Stripe can't charge under $0.50, so fully/nearly-fully discounted orders
    // skip payment; /lob ships them only when a server-validated promo is on
    // the PaymentIntent ($is_free_with_promo).
    const promoApplied = Object.keys(promoCode).length > 0;
    if( cost === 0 || (cost < 50 && promoApplied) ){
      setPaymentSuccessful(true);
      await sendPostcard(paymentIntent);
      setIsLoading(false);
      return;
    }

    if (elements == null) {
      setIsLoading(false);
      return;
    }

    // Stash the order so a redirect-based payment method (e.g. crypto) can
    // finish the /lob submission after the round-trip; the in-page outcomes
    // below all clear it.
    try {
      sessionStorage.setItem(PENDING_ORDER_KEY, JSON.stringify({ ts: Date.now(), body: buildLobBody() }));
    } catch (e) { /* storage unavailable: redirect methods will show the no-stash notice */ }

    // Name + email are collected by our own form fields, so the Payment
    // Element is configured not to collect them (fields.billingDetails) and
    // they're passed here instead. Wallets/redirect methods supply their own.
    const { error, paymentIntent: confirmed } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: {
          billing_details: { name: payerName, email: email },
        },
      },
    });

    if (error) {
      sessionStorage.removeItem(PENDING_ORDER_KEY);
      if (error.type === 'card_error' || error.type === 'validation_error') {
        setMessageWithType(error.message, 'error');
      } else {
        setMessageWithType('An unexpected error occurred. Please try again.', 'error');
      }
    }
    else if (confirmed && confirmed.status === 'succeeded') {
      sessionStorage.removeItem(PENDING_ORDER_KEY);
      setMessageWithType('Payment successful! You\'ll receive an email receipt shortly.', 'success');
      setPaymentSuccessful(true);
      await sendPostcard(confirmed);
    }
    else if (confirmed && confirmed.status === 'processing') {
      setMessageWithType('Payment is being processed. We\'ll update you when it\'s complete.', 'success');
    }
    else if (confirmed && confirmed.status === 'requires_payment_method') {
      sessionStorage.removeItem(PENDING_ORDER_KEY);
      setMessageWithType('Payment failed. Please try a different payment method.', 'error');
    }
    else {
      sessionStorage.removeItem(PENDING_ORDER_KEY);
      setMessageWithType('An unexpected error occurred. Please try again.', 'error');
    }

    setIsLoading(false);

  };

  const artistDomain = artistDomainFromUrl();

  // Show loading state while fetching cost
  if (defaultCost === 0) {
    return (
      <div className="stripecontainer">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading pricing information...
        </div>
      </div>
    );
  }

  return (
    <div className="stripecontainer">
      <h2 className="total">Total: {new Intl.NumberFormat('en-US', {
          currency: 'USD',
          style: 'currency'
      }).format(cost / 100)}</h2>
      <div className="description">Complete your payment to send your postcard. Shipping rates may vary by location.</div>

      <form className="stripeform" onSubmit={handleSubmit}>
        {/* Payer name field */}
        <div className="form-row">
          <label htmlFor='payerName'>
            Your name<small>(for payment)</small>
          </label>
          <input
            type="text"
            id='payerName'
            onChange={ e => {
              setPayerName(e.target.value)
              // Clear name-related errors when typing
              if (message && (message.includes('name') || message.includes('Name'))) {
                setMessageWithType('');
              }
            } }
            value={payerName}
            placeholder="Enter your full name"
          />
        </div>

        {/* Email field */}
        <div className="form-row">
          <label htmlFor='email'>
            Email<small>(for receipt)</small>
          </label>
          <input
            type="email"
            id='email'
            onChange={ e => {
              setEmail(e.target.value)
              // Clear email-related errors when typing
              if (message && (message.includes('email') || message.includes('Email'))) {
                setMessageWithType('');
              }
            } }
            value={email}
            placeholder="Enter your email address"
          />
        </div>

        {/* Newsletter opt-in — explicit consent to share the buyer's email
            with the artist whose postcard they're buying. Rendered only when
            the domain turned the feature on in the admin editor. */}
        {newsletterEnabled && artistDomain && (
          <div className="form-row optin-row">
            <label className="optin-label" htmlFor="newsletterOptIn">
              <input
                type="checkbox"
                id="newsletterOptIn"
                checked={newsletterOptIn}
                onChange={ e => setNewsletterOptIn(e.target.checked) }
              />
              <span>
                Email me occasional updates from {artistDomain}
                <small>(shares your email address with the artist)</small>
              </span>
            </label>
          </div>
        )}

        {/* Promo code section */}
        <div className="promo-section">
          <input
            type="text"
            value={promoInput}
            onChange={(e) => setPromoInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                validatePromoCode();
              }
            }}
            placeholder="Enter promo code"
            disabled={promoValidating || Object.keys(promoCode).length > 0}
          />

          {Object.keys(promoCode).length === 0 ? (
            <button
              type="button"
              onClick={validatePromoCode}
              disabled={promoValidating || promoInput.trim().length === 0}
            >
              {promoValidating ? 'Validating...' : 'Apply'}
            </button>
          ) : (
            <button
              type="button"
              onClick={clearPromoCode}
              className="remove"
            >
              Remove
            </button>
          )}
        </div>

        {/* One Payment Element replaces the old CardElement + hand-rolled
            Apple Pay button: cards, wallets, Link, and (once enabled in the
            Stripe dashboard) crypto all render inside it. Hidden for sub-50¢
            orders, which skip Stripe entirely. */}
        {cost >= 50 && (
          <div className="payment-element-container">
            <PaymentElement
              options={{
                layout: 'tabs',
                fields: {
                  billingDetails: { name: 'never', email: 'never' },
                },
              }}
            />
          </div>
        )}

        {/* Pay on your phone: this order, payment only. Not offered for
            sub-50¢ orders (nothing to pay) or once this screen is paying. */}
        {cost >= 50 && !isLoading && !paymentSuccessful && (
          <PayOnPhone
            paymentIntentId={paymentIntent.id}
            nonce={props.nonce}
            order={handoffOrder}
            kiosk={props.kiosk}
            onActive={props.onPayQrActive}
          />
        )}

        <button
          type="submit"
          className={isLoading ? 'loading' : (paymentSuccessful ? 'paid' : "")}
          disabled={isLoading || !stripe || (cost >= 50 && !elements) || paymentSuccessful}
        >
          {isLoading ? ButtonLabelVerifying('Processing Payment') : (paymentSuccessful ? ButtonLabelVerified('Payment Complete') : ButtonLabelVerify(`Pay ${new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(cost / 100)}`)) }
        </button>
      </form>

      {/* Show any error or success messages - now after the form */}
      {message && (
        <div id="payment-message" className={messageType} dangerouslySetInnerHTML={{ __html: message }}></div>
      )}

      {/* Once the order is fully placed (same moment the final receipt message
          appears), offer a way back to the artist's site. */}
      {orderComplete && <ReturnBand />}
    </div>
  );
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Landing view for redirect-based payment methods (crypto etc.): Stripe sends
// the buyer back with payment_intent_client_secret + redirect_status in the
// query string. The order payload was stashed in sessionStorage before the
// redirect; once the PaymentIntent reports succeeded, submit it to /lob. /lob
// has a server-side replay guard (PI metadata.lob_id), so a refresh here can't
// ship a duplicate postcard.
export function RedirectReturn() {
  const [phase, setPhase] = useState('working');
  const [receiptHtml, setReceiptHtml] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const clientSecret = params.get('payment_intent_client_secret');
      const redirectStatus = params.get('redirect_status');

      let stash = null;
      try { stash = JSON.parse(sessionStorage.getItem(PENDING_ORDER_KEY) || 'null'); } catch (e) { stash = null; }
      const pending = stash && stash.ts && (Date.now() - stash.ts) < PENDING_ORDER_TTL_MS ? stash.body : null;

      if (redirectStatus === 'failed' || !clientSecret) {
        sessionStorage.removeItem(PENDING_ORDER_KEY);
        if (!cancelled) setPhase('failed');
        return;
      }

      const stripe = await stripePromise;
      // Some methods land back here while the payment is still settling; poll
      // briefly before giving a "still processing" answer.
      let pi = null;
      for (let attempt = 0; attempt < 30; attempt++) {
        try {
          const res = await stripe.retrievePaymentIntent(clientSecret);
          pi = res.paymentIntent || null;
        } catch (e) { pi = null; break; }
        if (!pi || pi.status !== 'processing') break;
        if (!cancelled) setPhase('processing');
        await new Promise((r) => setTimeout(r, 2000));
        if (cancelled) return;
      }

      if (pi && pi.status === 'processing') { if (!cancelled) setPhase('still-processing'); return; }
      if (!pi || pi.status !== 'succeeded') {
        sessionStorage.removeItem(PENDING_ORDER_KEY);
        if (!cancelled) setPhase('failed');
        return;
      }
      if (!pending) { if (!cancelled) setPhase('no-stash'); return; }

      try {
        const nonceRes = await fetch(process.env.REACT_APP_BACKEND_URL + '/nonce', {
          method: 'POST',
          mode: 'cors',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
        });
        const nonceData = await nonceRes.json();

        const decoded = await postLob({ ...pending, nonce: nonceData.nonce, paymentIntent: { id: pi.id } });

        if (decoded && decoded.url) {
          sessionStorage.removeItem(PENDING_ORDER_KEY);
          // Strip Stripe's return params so a refresh shows the normal page
          // instead of re-entering this flow.
          const url = new URL(window.location.href);
          ['payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach((k) => url.searchParams.delete(k));
          window.history.replaceState(null, '', url.toString());
          track('purchase_completed', {
            cost: decoded.cost,
            original_cost: decoded.original_cost,
            country: (pending.country) || 'US',
            promo_used: !!(pending.promo && pending.promo.code),
            promo_code: (pending.promo && pending.promo.code) || null,
            postcard_id: decoded.id,
          });
          if (!cancelled) { setReceiptHtml(receiptMessage(pending.email, decoded)); setPhase('done'); }
        } else {
          // Keep the stash: a refresh retries the /lob submission.
          if (!cancelled) setPhase('lob-failed');
        }
      } catch (e) {
        if (!cancelled) setPhase('lob-failed');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const retryUrl = (() => {
    const url = new URL(window.location.href);
    ['payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach((k) => url.searchParams.delete(k));
    return url.toString();
  })();

  return (
    <div className="stripecontainer" style={{ maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
      {(phase === 'working' || phase === 'processing') && (
        <p>Finishing your payment…</p>
      )}
      {phase === 'still-processing' && (
        <p>Your payment is still processing. Refresh this page in a moment to complete your order — don't worry, it can't be submitted twice.</p>
      )}
      {phase === 'failed' && (
        <div id="payment-message" className="error">
          Your payment was not completed. <a href={retryUrl}>Return to checkout to try again</a>.
        </div>
      )}
      {phase === 'no-stash' && (
        <div id="payment-message" className="error">
          Your payment was received, but we couldn't find your order details in this browser.
          Please contact <a href="mailto:support@olliemail.net">support@olliemail.net</a> and we'll finish your order or refund you.
        </div>
      )}
      {phase === 'lob-failed' && (
        <div id="payment-message" className="error">
          Your payment went through but we hit a problem submitting your postcard.
          Refresh this page to retry, or contact <a href="mailto:support@olliemail.net">support@olliemail.net</a>.
        </div>
      )}
      {phase === 'done' && (
        <>
          <div id="payment-message" className="success" dangerouslySetInnerHTML={{ __html: receiptHtml }}></div>
          <ReturnBand />
        </>
      )}
    </div>
  );
}

export default function Stripe(props) {
  if( props.addressVerified ){
    const clientSecret = props.elementsOptions.paymentIntent.client_secret;
    return (
      // One PaymentIntent for the life of the checkout: amount changes are
      // server-side updates + elements.fetchUpdates(), never a new secret, so
      // Elements mounts once and form state survives repricing.
      <Elements stripe={stripePromise} options={{ clientSecret: clientSecret }}>
        <CheckoutForm
          billingDetails={props.billingDetails}
          paymentIntent={ props.elementsOptions.paymentIntent }
          email={props.email}
          setEmail={props.setEmail}
          payerName={props.payerName}
          setPayerName={props.setPayerName}
          setPaymentIntent={props.elementsOptions.setPaymentIntent}
          onBusyChange={props.onBusyChange}
          kiosk={props.kiosk}
          nonce={props.nonce}
          onPayQrActive={props.onPayQrActive}
          onOrderComplete={props.onOrderComplete}
        />
      </Elements>
    );
  }
};
